/**
 * Ruoli di un account (V51): le assegnazioni per stagione, con la corrente
 * in testa. Si aggiungono e si revocano Mister e Dirigente; l'Admin Squadra
 * compare in sola lettura. Ogni modifica finisce nel registro attività.
 *
 * Le squadre-stagione proposte sono quelle del selettore, che per l'Admin
 * sono tutte quelle della societa, tolte quelle dove l'account ha gia un
 * ruolo.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { InfoIcon, UserMinusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ComandoIcona, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { FormRuolo, type GruppoOpzioni } from './FormRuolo.js';
import {
  type AccountSocieta,
  ETICHETTA_RUOLO,
  type RuoloAssegnato,
  eAdmin,
  nomeSquadra,
  ruoliPerStagione,
  squadreAssegnabili,
} from './ruoli.js';

export function RuoliAccount() {
  const { id } = useParams();
  const { squadre, ricaricaSquadre } = useContesto();
  const {
    dati: account,
    errore,
    ricarica,
  } = useDati<AccountSocieta>(`/amministrazione/account/${id}`);
  const [aggiunta, setAggiunta] = useState(false);
  const [inRevoca, setInRevoca] = useState<string | null>(null);

  if (!account)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const persona = `${account.nome} ${account.cognome}`;
  const assegnabili = squadreAssegnabili(account, squadre ?? []);
  const gruppi: GruppoOpzioni[] = [];
  for (const s of assegnabili) {
    let g = gruppi.find((x) => x.titolo === s.stagione);
    if (!g) gruppi.push((g = { titolo: s.stagione, opzioni: [] }));
    g.opzioni.push({ valore: s.id, etichetta: nomeSquadra(s) });
  }

  // Un ruolo tolto o dato a chi sta usando l'app cambia anche il suo
  // selettore: se e l'Admin stesso, lo si vede subito.
  const aggiorna = () => Promise.all([ricarica(), ricaricaSquadre()]);

  const revoca = async (r: RuoloAssegnato) => {
    setInRevoca(r.id);
    try {
      await chiama(`/amministrazione/account/${account.id}/ruoli/${r.id}`, { metodo: 'DELETE' });
      await aggiorna();
      toast.success('Ruolo revocato.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInRevoca(null);
    }
  };

  const perStagione = ruoliPerStagione(account.ruoli);
  const aggiungi = (
    <PulsanteNuovo
      etichetta="Aggiungi ruolo"
      onClick={() => setAggiunta(true)}
      disabled={assegnabili.length === 0}
    />
  );

  return (
    <>
      <Testata titolo={persona} azione={aggiungi} indietro="/" />

      <Corpo className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {account.email}
          {!account.attivo && <Badge variant="secondary">Disattivato</Badge>}
        </div>

        {eAdmin(account) && (
          <Alert>
            <InfoIcon />
            <AlertTitle>Admin Squadra</AlertTitle>
            <AlertDescription>
              {persona} è l'Admin Squadra della società. Questo ruolo lo assegna e lo revoca il
              gestore della piattaforma.
            </AlertDescription>
          </Alert>
        )}

        {!account.attivo && (
          <p className="text-sm text-muted-foreground">
            L'account è disattivato: i ruoli restano, ma valgono solo dopo la riattivazione.
          </p>
        )}

        {perStagione.length === 0
          ? // All'Admin basta l'avviso sopra: vede gia tutte le squadre.
            !eAdmin(account) && (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>Nessun ruolo di squadra</EmptyTitle>
                  <EmptyDescription>
                    Senza un ruolo l'account entra ma non vede nessuna squadra. Nominalo Mister o
                    Dirigente di una squadra-stagione.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )
          : perStagione.map((g) => (
              <section key={g.stagione} className="flex flex-col gap-2">
                <h2 className="flex items-center gap-2 font-semibold">
                  {g.stagione}
                  {g.corrente && <Badge>Corrente</Badge>}
                </h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Squadra</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead>
                        <span className="sr-only">Azioni</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.ruoli.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{nomeSquadra(r)}</TableCell>
                        <TableCell>{ETICHETTA_RUOLO[r.ruolo]}</TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <ComandoIcona
                              etichetta="Revoca il ruolo"
                              Icona={UserMinusIcon}
                              size="icon-sm"
                              disabled={inRevoca === r.id}
                              onClick={() => void revoca(r)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            ))}
      </Corpo>

      <Dialog open={aggiunta} onOpenChange={setAggiunta}>
        <DialogContent>
          {aggiunta && (
            <FormRuolo
              titolo={`Nuovo ruolo per ${persona}`}
              descrizione="Il ruolo vale su una sola squadra-stagione."
              etichettaScelta="Squadra-stagione"
              gruppi={gruppi}
              onChiudi={() => setAggiunta(false)}
              onInvia={async (ruolo, squadraStagioneId) => {
                await chiama(`/amministrazione/account/${account.id}/ruoli`, {
                  metodo: 'POST',
                  corpo: { ruolo, squadraStagioneId },
                });
                setAggiunta(false);
                await aggiorna();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
